import React, { Dispatch, useEffect, useMemo, useState } from 'react';

import {
    ProgramType,
    ProgramTypeNameEnum,
    ServiceLaunch
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import Tooltip from '@amzn/meridian/tooltip';

import { isEmpty, isUndefined } from 'lodash';
import { Input } from 'src/components/Input';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { ContractTermSection } from 'src/components/shared/ContractTermSection';
import { ProgramTypeLabel } from 'src/constants';
import { IMetadata } from 'src/types/IMetadata';
import { IPrimeAMFRateCardValidationAction } from 'src/types/IPrimeAMFRateCardValidationAction';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues } from 'src/types/IRateCard';

import { IPrimeAMFRateCardErrors } from '../../rateCardValidationReducer';

interface IAddProgramType {
    rateCardValues: IPrimeAMFRateCardValues;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
    rateCardErrors: IPrimeAMFRateCardErrors;
    dispatchRateCardErrors: Dispatch<IPrimeAMFRateCardValidationAction>;
    programTypes: ProgramType[];
    augmentedMetadata: IMetadata;
}

export const AddProgramType: React.FC<IAddProgramType> = ({
    rateCardValues,
    dispatchRateCardValues,
    rateCardErrors,
    dispatchRateCardErrors,
    programTypes,
    augmentedMetadata
}) => {
    // Ignore DEFAULT and KEMP
    const hasProgramTypes = !isUndefined(
        rateCardValues.primeContractTerms?.costPerPlay?.programTypeList?.find((pt) =>
            [ProgramTypeNameEnum.DEFAULT, ProgramTypeNameEnum.KEMP].every((name) => name !== pt.programTypeName)
        )
    );
    const [isToggled, setIsToggled] = useState(hasProgramTypes);
    const errorMessage = rateCardErrors.primeContractTerms.costPerPlay.programTypes;

    const [launchedTerritoryList, setLaunchedTerritoryList] = useState<ServiceLaunch[]>([]);

    const householdSharingMultiplierValue = useMemo(() => {
        const hhProgramType = rateCardValues.primeContractTerms?.costPerPlay?.programTypeList?.find(
            (pt) => pt.programTypeName === ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
        );

        if (isUndefined(hhProgramType)) {
            return '';
        }

        return hhProgramType.programTypeDefinitions![0].value;
    }, [rateCardValues.primeContractTerms?.costPerPlay?.programTypeList]);

    useEffect(() => {
        const serviceLaunches = augmentedMetadata.activeServiceLaunchDateMapping![rateCardValues.serviceId].filter(
            (territory) => territory.territoryCode !== 'IN'
        );
        setLaunchedTerritoryList(serviceLaunches);
    }, [augmentedMetadata.activeServiceLaunchDateMapping]);

    // Validate on rate card/toggle change
    useEffect(() => {
        dispatchRateCardErrors({
            type: 'validate_program_type',
            rateCardValues,
            isToggled
        });
    }, [rateCardValues.primeContractTerms?.costPerPlay?.programTypeList]);

    const containsProgramType = (name: ProgramTypeNameEnum): boolean =>
        rateCardValues.primeContractTerms?.costPerPlay?.programTypeList?.some(
            (programType) => programType.programTypeName === name
        ) ?? false;

    const handleToggle = (): void => {
        if (isToggled) {
            dispatchRateCardValues({ type: 'reset_program_types' });
        }

        setIsToggled((prevIsToggled) => !prevIsToggled);
    };

    const handleProgramTypeChange = (programType: ProgramType, shouldBeChecked: boolean): void => {
        dispatchRateCardValues({
            type: 'set_program_type',
            programType: programType,
            shouldBeIncluded: shouldBeChecked
        });
    };

    const handleMultiplierBlur = (): void => {
        const householdProgramType = rateCardValues.primeContractTerms?.costPerPlay?.programTypeList?.find(
            (pt) => pt.programTypeName === ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
        );

        if (isUndefined(householdProgramType)) {
            return;
        }

        // If input is empty, default to 1
        if (isEmpty(householdProgramType.programTypeDefinitions![0].value)) {
            const programTypeToInsert: ProgramType = {
                ...householdProgramType,
                programTypeDefinitions: [
                    {
                        ...householdProgramType.programTypeDefinitions![0],
                        value: '1'
                    }
                ]
            };

            dispatchRateCardValues({
                type: 'set_program_type',
                programType: programTypeToInsert,
                shouldBeIncluded: true
            });
        }
    };

    const handleMultiplierChange = (val: string): void => {
        const householdProgramType = rateCardValues.primeContractTerms?.costPerPlay?.programTypeList?.find(
            (pt) => pt.programTypeName === ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
        );

        if (isUndefined(householdProgramType)) {
            return;
        }

        // Insert new value into household definition
        const programTypeToInsert: ProgramType = {
            ...householdProgramType,
            programTypeDefinitions: [
                {
                    ...householdProgramType.programTypeDefinitions![0],
                    value: val.substring(0, 8)
                }
            ]
        };

        dispatchRateCardValues({
            type: 'set_program_type',
            programType: programTypeToInsert,
            shouldBeIncluded: true
        });
    };

    const handleTerritoryToggle = (territoryId: number, shouldBeToggled: boolean): void => {
        const householdSharingTerritoryIds = new Set(
            rateCardValues.primeContractTerms?.costPerPlay?.householdSharingTerritoryIds
        );

        if (shouldBeToggled) {
            householdSharingTerritoryIds.add(territoryId);
        } else {
            householdSharingTerritoryIds.delete(territoryId);
        }

        dispatchRateCardValues({
            type: 'set_household_sharing_territories',
            householdSharingTerritoryIds
        });
    };

    return (
        <ContractTermSection
            title="Program type"
            subTitle="Enable different rates per program type (DE Radio Plays, Household Sharing, etc.)"
            isToggled={isToggled}
            handleToggle={handleToggle}
        >
            {isToggled && (
                <>
                    <Column spacing="300">
                        <Text type="h300">Select program types</Text>
                        {programTypes
                            .filter((programType) =>
                                [
                                    ProgramTypeNameEnum.DE_RADIO_PLAY,
                                    ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
                                ].some((name) => name === programType.programTypeName)
                            )
                            .map((programType) => (
                                <Checkbox
                                    key={programType.programTypeName}
                                    data-testid={programType.programTypeName}
                                    checked={containsProgramType(programType.programTypeName as ProgramTypeNameEnum)}
                                    onChange={(val) => handleProgramTypeChange(programType, val)}
                                >
                                    {ProgramTypeLabel[programType.programTypeName as keyof typeof ProgramTypeLabel]}
                                </Checkbox>
                            ))}
                    </Column>
                    {!isEmpty(errorMessage) && (
                        <Alert type="error" size="small">
                            {errorMessage}
                        </Alert>
                    )}
                    {isEmpty(errorMessage) &&
                        containsProgramType(ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY) && (
                            <Column spacing="300">
                                <Text type="h300">Define program type</Text>
                                <Row alignmentVertical="top" widths={['50%', '50%']}>
                                    <Column spacing="100">
                                        <Text type="b200">Program type</Text>
                                        <Text type="b300">
                                            {ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY}
                                        </Text>
                                    </Column>
                                    <Input
                                        label="Multiplier"
                                        isRequired={true}
                                        value={householdSharingMultiplierValue}
                                        isEditMode={true}
                                        type="number"
                                        onChange={handleMultiplierChange}
                                        onBlur={handleMultiplierBlur}
                                    />
                                </Row>
                                <Table headerRows={1} spacing={'small'} showStripes={true}>
                                    <TableRow>
                                        <TableCell>
                                            <Row spacing="200">
                                                <Text type="h100">Territory</Text>
                                                <Tooltip
                                                    title="Territories configured for Prime household sharing."
                                                    position="end"
                                                >
                                                    <Button type="link" size="small">
                                                        <Icon tokens={infoKnockoutTokens} />
                                                    </Button>
                                                </Tooltip>
                                            </Row>
                                        </TableCell>
                                        <TableCell>
                                            <Row spacing="200">
                                                <Text type="h100">Enabled for household sharing</Text>
                                                <Tooltip
                                                    title="Territories where household sharing is launched are enabled by default."
                                                    position="end"
                                                >
                                                    <Button type="link" size="small">
                                                        <Icon tokens={infoKnockoutTokens} />
                                                    </Button>
                                                </Tooltip>
                                            </Row>
                                        </TableCell>
                                    </TableRow>
                                    {launchedTerritoryList.map(({ territoryID, territoryCode }) => (
                                        <TableRow key={territoryID}>
                                            <TableCell>
                                                <TerritoryFlag
                                                    territoryCode={territoryCode!}
                                                    showTerritoryName={true}
                                                    showTerritoryCode={false}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Toggle
                                                    checked={rateCardValues.primeContractTerms?.costPerPlay?.householdSharingTerritoryIds?.has(
                                                        territoryID!
                                                    )}
                                                    onChange={(val) => handleTerritoryToggle(territoryID!, val)}
                                                    data-testid={`household-sharing-toggle-${territoryCode}`}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </Table>
                            </Column>
                        )}
                </>
            )}
        </ContractTermSection>
    );
};
